import CommonButton from 'components/Buttons/CommonButton';
import TextInput from 'components/Input/TextInput';
import { Form, Formik } from 'formik';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import services from 'services';
import action from 'state/actions';
import selector from 'state/selectors';
import utils from 'utils';
import styled from 'styled-components/macro';
import { modalActionType, notDraggable } from 'utils/constants';
import * as Yup from 'yup';

import Modal from './';

const InputModalContainer = styled.div`
  margin-bottom: 28px;
`;

const ButtonContainer = styled.div`
  text-align: center;
  display: flex;
  align-content: center;
  justify-content: center;
  > button {
    margin: 0 10px;
  }
`;

interface UserPassword {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

const initialValues: UserPassword = {
  oldPassword: '',
  newPassword: '',
  newPasswordConfirm: '',
};

interface Props {
  visible?: boolean;
  userCode?: number;
  dismiss?: Function;
}

const PasswordModal = (props: Props) => {
  const userCode = useSelector(selector.user.getIdUser) || -1;
  const dispatch = useDispatch();
  const t = utils.intl.useTranslator();

  const activeModalType = useSelector(selector.modal.getActionType);
  const modalVisibility = useSelector(selector.modal.getVisibility);
  const closeModal = () => dispatch(action.modal.closeModal());
  const isModalDraggable = notDraggable.includes(activeModalType || '');

  const onSubmit = async (values: UserPassword) => {
    if (props.userCode || userCode) {
      try {
        const { data } = await services.changePassword(
          props.userCode || userCode,
          values.oldPassword,
          values.newPassword,
        );
        if (data.pwdIsChanged) {
          closeModal();
          utils.app.notify('success', t('password-changed'));
          props.dismiss && props.dismiss();
        } else {
          utils.app.notify('warning', data.message);
        }
      } catch (error) {
        utils.app.notify('fail', error as Error);
      }
    }
  };

  return (modalVisibility && activeModalType && activeModalType === modalActionType.USER_PASSWORD) || props.visible ? (
    <Modal open={modalVisibility} onClose={closeModal} style={{ width: '400px' }} isDraggable={!isModalDraggable}>
      <>
        <Modal.Header title={t('changePassword')} subtitle={t('change-password-description')} />
        <Formik
          validationSchema={Yup.object().shape({
            oldPassword: Yup.string().required('Required'),
            newPassword: Yup.string().required('Required'),
            newPasswordConfirm: Yup.string()
              .required('Required')
              .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
          })}
          onSubmit={onSubmit}
          initialValues={{ ...initialValues }}
        >
          {(formikProps) => {
            const { values, errors, touched, isValid, isValidating, dirty, handleChange, handleBlur } = formikProps;
            return (
              <Form autoComplete="new-password">
                <InputModalContainer>
                  <TextInput
                    id="oldPassword"
                    hasFeedback={errors['oldPassword'] && touched['oldPassword'] ? true : false}
                    feedbackMessage={errors['oldPassword']}
                    value={values['oldPassword']}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={t('old-password')}
                    type="password"
                  />
                </InputModalContainer>
                <InputModalContainer>
                  <TextInput
                    id="newPassword"
                    hasFeedback={errors['newPassword'] && touched['newPassword'] ? true : false}
                    feedbackMessage={errors['newPassword']}
                    value={values['newPassword']}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={t('new-password')}
                    type="password"
                  />
                </InputModalContainer>
                <InputModalContainer>
                  <TextInput
                    id="newPasswordConfirm"
                    hasFeedback={errors['newPasswordConfirm'] && touched['newPasswordConfirm'] ? true : false}
                    feedbackMessage={errors['newPasswordConfirm']}
                    value={values['newPasswordConfirm']}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label={t('new-password-confirm')}
                    type="password"
                  />
                </InputModalContainer>
                <ButtonContainer>
                  <CommonButton
                    id="saveButton"
                    scope="primary"
                    disabled={!dirty || !isValid || isValidating}
                    type="submit"
                    value={t('save')}
                  />
                  <CommonButton
                    id="cancelButton"
                    scope="secondary"
                    type="button"
                    value={t('cancel')}
                    action={() => {
                      closeModal();
                    }}
                  />
                </ButtonContainer>
              </Form>
            );
          }}
        </Formik>
      </>
    </Modal>
  ) : null;
};

export default PasswordModal;
